'use client'

import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import { MotionConfig } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useToast } from '@/components/ui/Toast'
import { errorMessage } from '@/lib/errorMessage'

/**
 * Module-level bridge from the React-Toast context into the QueryClient's
 * MutationCache (which is constructed once, outside React, so it can't call a
 * hook). QueryProvider populates this in an effect; the global onError reads it.
 * Until the provider mounts it stays a no-op, so nothing crashes.
 */
let toastError: (message: string) => void = () => {}

export default function QueryProvider({ children }: { children: React.ReactNode }) {
  // Intentionally do NOT set `history.scrollRestoration = 'manual'` or
  // call `window.scrollTo(0, 0)` on mount. Letting Next.js's
  // `experimental.scrollRestoration: true` (in next.config.js) handle it
  // restores the previous scroll position on browser back/forward, which
  // is the expected browser default behavior.

  const toast = useToast()

  // Keep the module-level bridge pointed at the live toast dispatcher.
  useEffect(() => {
    toastError = toast.error
    return () => {
      toastError = () => {}
    }
  }, [toast])

  const [queryClient] = useState(
    () =>
      new QueryClient({
        // App-wide safety net: any mutation that does NOT define its own
        // onError surfaces a toast, so a failed write (delete / approve / rotate
        // secret / disconnect / save) is never completely silent. The
        // cache-level handler runs for every mutation error, so to avoid a
        // double message we defer to a mutation's own onError when it has one
        // (it can show scoped/custom copy); this only fires when none is set.
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            if (mutation.options.onError) return
            toastError(errorMessage(error))
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
            retry: 2,
            retryDelay: 3000,
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {/* MotionConfig honours the OS-level prefers-reduced-motion setting:
          when the user opts out of motion, framer-motion transforms become
          instant. CSS animations are already handled by the @media rule
          in globals.css. */}
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </QueryClientProvider>
  )
}
